import { useRef, type FC, type ReactNode } from 'react';
import { useFragment, graphql } from 'react-relay';

import { FormattedMessage } from 'dibs-react-intl';
import { Button } from 'dibs-elements/exports/Button.tsx';
import { trackEvent } from 'dibs-tracking';
import { BuyerProductTileShimmer } from 'dibs-buyer-product-tile/exports/BuyerProductTileShimmer.ts';
import { SharedItemTile } from '../../shared/SharedItemTile/SharedItemTile.tsx';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider.tsx';
import { QuickViewProvider } from 'dibs-buyer-product-tile/exports/QuickView.ts';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { useBuyerProductTileSize } from '../../shared/hooks/useBuyerProductTileSize.ts';

import {
    VERTICAL_OFFSET_SB_RESP_IMG,
    VERTICAL_OFFSET_MOBILE_IMG,
} from '../../constants/imageConstants.js';
import { locations } from '../../utils/tracking/shared/favoritesTracking.tsx';
import { useSbSelector } from '../../reducers/useSbSelector.ts';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import { useSbSharedItemTracking } from '../hooks/sharedItemTracking/useSbSharedItemTracking.ts';
import { useClientState } from 'dibs-react-hooks/exports/useClientState.ts';
import { getIsOpenPdpInNewTabVariant } from '../../utils/abTestHelper.ts';

import { type FavoritesProviderChildrenProps } from '../../components/global/FavoriteIcons/FavoritesProviderChildrenProps.ts';
import { type SbSharedCreatorsResultContainer_viewer$key } from './__generated__/SbSharedCreatorsResultContainer_viewer.graphql.ts';
import { type SbSharedCreatorsResultContainer_itemSearch$key } from './__generated__/SbSharedCreatorsResultContainer_itemSearch.graphql.ts';
import { type SbSharedCreatorsResultContainer_items$key } from './__generated__/SbSharedCreatorsResultContainer_items.graphql.ts';

import dibsCss from 'dibs-css';
import classnames from 'classnames';
import styles from './SbSharedCreatorsResultContainer.scss';

const ITEMS_PER_ROW = 4;
const LAZY_LOAD_ITEM_TILE_INDEX = ITEMS_PER_ROW * 3;

const viewerFragment = graphql`
    fragment SbSharedCreatorsResultContainer_viewer on Viewer {
        ...SharedItemTile_viewer
        ...useSbSharedItemTracking_viewer
    }
`;
const itemSearchFragment = graphql`
    fragment SbSharedCreatorsResultContainer_itemSearch on ItemSearchQueryConnection {
        ...SharedItemTile_itemSearch
        ...useSbSharedItemTracking_itemSearch
        meta {
            header
        }
    }
`;
const itemsFragment = graphql`
    fragment SbSharedCreatorsResultContainer_items on ItemSearchResult @relay(plural: true) {
        item {
            serviceId
            ...FavoritesProvider_item
            ...useSbSharedItemTracking_item
            ...SharedItemTile_item
                @arguments(
                    isTrade: $isTrade
                    priceBookName: $priceBookName
                    showSeller: $showSeller
                )
        }
    }
`;

type Props = {
    viewer: SbSharedCreatorsResultContainer_viewer$key;
    itemSearch: SbSharedCreatorsResultContainer_itemSearch$key;
    items: SbSharedCreatorsResultContainer_items$key | null | undefined;
    vertical: ReactNode;
    link: string;
};

export const SbSharedCreatorsResultContainer: FC<Props> = ({
    viewer: viewerRef,
    items: itemsRef,
    itemSearch: itemSearchRef,
    vertical,
    link,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const itemNodes = useFragment(itemsFragment, itemsRef);

    const { isMobile } = useServerVarsContext();
    const afterDisplay = useSbSelector(state => state.relayVariables.variables.afterDisplay);
    const userId = useSbSelector(state => state.relayVariables.variables.userId);
    const refetchInFlight = useSbSelector(state => state.filters.refetchInFlight);
    const tileSize = useBuyerProductTileSize();
    const gridContainerRef = useRef<HTMLDivElement | null>(null);

    const items = itemNodes?.map(({ item }) => item).filter(filterFalsy) || [];
    const { fireItemImpressionTracking, fireItemClickTracking } = useSbSharedItemTracking({
        viewer,
        itemSearch,
        items,
    });

    const isClient = useClientState();
    const isOpenPdpInNewTabVariant = isClient && getIsOpenPdpInNewTabVariant();

    return (
        <>
            <div
                className={classnames(styles.grid, dibsCss.grid, dibsCss.gapSmall)}
                ref={gridContainerRef}
            >
                <FavoritesProvider
                    disable={!afterDisplay}
                    items={items}
                    location={locations.SEARCH_BROWSE}
                    userId={userId}
                    fetchFolder={!isMobile}
                >
                    {({ props: favorites }: { props: FavoritesProviderChildrenProps }) => (
                        <QuickViewProvider elementRef={gridContainerRef}>
                            {items.map((item, index) => {
                                if (refetchInFlight && !isMobile) {
                                    return <BuyerProductTileShimmer key={index} size={tileSize} />;
                                }
                                return (
                                    <SharedItemTile
                                        itemSearch={itemSearch}
                                        key={item?.serviceId || index}
                                        item={item}
                                        user={null}
                                        favorites={favorites}
                                        index={index}
                                        lazyLoadStartIndex={
                                            isMobile ? undefined : LAZY_LOAD_ITEM_TILE_INDEX
                                        }
                                        onContentVisible={() =>
                                            fireItemImpressionTracking({
                                                itemId: item?.serviceId,
                                                index,
                                            })
                                        }
                                        onClick={() =>
                                            fireItemClickTracking({
                                                itemId: item?.serviceId,
                                                index,
                                            })
                                        }
                                        isOpenPdpInNewTabVariant={isOpenPdpInNewTabVariant}
                                        imageLoadVerticalOffset={
                                            isMobile
                                                ? VERTICAL_OFFSET_MOBILE_IMG
                                                : VERTICAL_OFFSET_SB_RESP_IMG
                                        }
                                        viewer={viewer}
                                    />
                                );
                            })}
                        </QuickViewProvider>
                    )}
                </FavoritesProvider>
            </div>
            <div className={classnames(styles.ctaWrapper, dibsCss.mbLarge, dibsCss.textCenter)}>
                <div className={classnames(dibsCss.sassyFontHeaderSizeSmall, dibsCss.mbSmall)}>
                    <FormattedMessage
                        id="sb.SbSharedCreatorsResultContainer.shopNowDescription"
                        defaultMessage="Browse all {vertical} from {creator}"
                        values={{ vertical, creator: itemSearch?.meta?.header }}
                    />
                </div>
                <Button
                    type="secondary"
                    size="medium"
                    href={link}
                    dataTn="shop-now-button"
                    className={styles.buttonText}
                    fullWidth={isMobile ? true : false}
                    onClick={() =>
                        trackEvent({
                            category: 'promo interaction',
                            action: 'creator shop now click',
                            label: 'none',
                        })
                    }
                >
                    <FormattedMessage
                        id="sb.SbSharedCreatorsResultContainer.shopNowCTA"
                        defaultMessage="Shop Now"
                    />
                </Button>
            </div>
        </>
    );
};
